/**
 * iSpring Access Control Script Generator
 *
 * Generates a JavaScript script to be injected into iSpring HTML documents
 * to enforce time-based access control (2h10 timer, 2h30 for ONCO'MMENCE meetings + meeting end + 15min grace period)
 */

// Access timer after opening (minutes), mirrored by SupportController::checkAccess() on the API
export const ACCESS_TIMER_MINUTES = 130 // 2h10
export const ONCOMMENCE_ACCESS_TIMER_MINUTES = 150 // 2h30 for ONCO'MMENCE meetings

/**
 * Converts an API date ("YYYY-MM-DD HH:mm:ss", Europe/Paris wall time) to an ISO UTC string,
 * whatever the browser timezone (a browser outside Paris time would otherwise shift the meeting end)
 *
 * @param {string} value - API date
 * @returns {string|null} ISO UTC string, or null if the date cannot be read
 */
export function parisDateToIso(value) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = String(value || '').split(/[- :T]/).map(Number)
    const asUtc = Date.UTC(y, mo - 1, d, h, mi, s)
    if (Number.isNaN(asUtc)) return null

    // Paris offset (summer / winter time) at a given instant
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Paris', hourCycle: 'h23',
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
    })
    const parisOffset = (instant) => {
        const parts = {}
        formatter.formatToParts(new Date(instant)).forEach(part => { parts[part.type] = Number(part.value) })
        return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second) - instant
    }

    // Second pass: the offset must be the one of the real instant (DST switch days)
    return new Date(asUtc - parisOffset(asUtc - parisOffset(asUtc))).toISOString()
}

/**
 * Generates the access control script to inject into the iSpring document
 *
 * @param {Object} config - Configuration object
 * @param {string} config.apiUrl - Base URL of the API (e.g., 'http://localhost:81')
 * @param {number} config.supportId - ID of the support
 * @param {string} config.type - Type of attachment ('pro_attachment', 'guide_attachment', 'user_attachment')
 * @param {number|null} config.meetingId - ID of the meeting (can be null)
 * @param {string|null} config.meetingEnd - ISO date string of meeting end (can be null)
 * @param {number} [config.maxDurationMinutes] - Access timer in minutes (default ACCESS_TIMER_MINUTES)
 * @returns {string} JavaScript code to inject
 */
export function generateAccessControlScript(config) {
    const maxDurationMinutes = config.maxDurationMinutes || ACCESS_TIMER_MINUTES
    const maxDurationLabel = Math.floor(maxDurationMinutes / 60) + 'h' + String(maxDurationMinutes % 60).padStart(2, '0')

    return `
(function() {
    'use strict';

    // ========================================
    // Configuration
    // ========================================
    const CONFIG = {
        apiUrl: '${config.apiUrl}',
        supportId: ${config.supportId},
        type: '${config.type}',
        meetingId: ${config.meetingId || 'null'},
        meetingEnd: ${config.meetingEnd ? `'${config.meetingEnd}'` : 'null'},
        serverCheckEnabled: ${config.serverCheckEnabled === false ? 'false' : 'true'},
        openedAt: Date.now(),
        maxDuration: ${maxDurationMinutes} * 60 * 1000, // ${maxDurationLabel} in milliseconds
        checkInterval: 60 * 1000, // Check every 60 seconds
        meetingGracePeriod: 15 * 60 * 1000 // 15 minutes after meeting end
    };

    // ========================================
    // State
    // ========================================
    let isExpired = false;
    let checkIntervalId = null;

    // ========================================
    // Create blocking overlay
    // ========================================
    function createOverlay() {
        const overlay = document.createElement('div');
        overlay.id = 'oncogite-access-overlay';
        overlay.innerHTML = \`
            <div style="
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(135deg, rgba(0, 0, 0, 0.97) 0%, rgba(30, 30, 30, 0.97) 100%);
                z-index: 2147483647;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                color: white;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            ">
                <div style="text-align: center; max-width: 500px; padding: 40px;">
                    <div style="font-size: 72px; margin-bottom: 24px;">
                        <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#f7921d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                    </div>
                    <h1 style="font-size: 32px; margin-bottom: 16px; font-weight: 600; color: #f7921d;">
                        Session expirée
                    </h1>
                    <p id="oncogite-expiry-reason" style="font-size: 18px; opacity: 0.9; margin-bottom: 24px; line-height: 1.6;">
                        Votre accès à ce support a expiré.
                    </p>
                    <p style="font-size: 14px; opacity: 0.6; margin-bottom: 32px; line-height: 1.5;">
                        Veuillez fermer cet onglet.<br>
                        Si vous avez besoin d'accéder à nouveau au support, rouvrez-le depuis l'application.
                    </p>
                    <button onclick="window.close()" style="
                        background: #f7921d;
                        color: white;
                        border: none;
                        padding: 16px 40px;
                        font-size: 16px;
                        font-weight: 600;
                        border-radius: 8px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        box-shadow: 0 4px 14px rgba(247, 146, 29, 0.4);
                    " onmouseover="this.style.background='#e8820d'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(247, 146, 29, 0.5)';" onmouseout="this.style.background='#f7921d'; this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 14px rgba(247, 146, 29, 0.4)';">
                        Fermer cet onglet
                    </button>
                </div>
            </div>
        \`;
        overlay.style.display = 'none';
        document.body.appendChild(overlay);
        return overlay;
    }

    // ========================================
    // Show expired overlay with specific message
    // ========================================
    function showExpiredOverlay(message) {
        if (isExpired) return; // Prevent multiple calls
        isExpired = true;

        const overlay = document.getElementById('oncogite-access-overlay');
        if (overlay) {
            const reasonEl = document.getElementById('oncogite-expiry-reason');
            if (reasonEl && message) {
                reasonEl.textContent = message;
            }
            overlay.style.display = 'block';

            // Block all interactions with content
            document.body.style.overflow = 'hidden';
            document.body.style.pointerEvents = 'none';
            overlay.style.pointerEvents = 'auto';
        }

        // Stop checks
        if (checkIntervalId) {
            clearInterval(checkIntervalId);
            checkIntervalId = null;
        }

        console.log('[OnCogite] Access expired:', message);
    }

    // ========================================
    // Local timer check (${maxDurationLabel})
    // ========================================
    function checkLocalTimer() {
        const elapsed = Date.now() - CONFIG.openedAt;
        if (elapsed >= CONFIG.maxDuration) {
            showExpiredOverlay('Le temps d\\'accès maximum de ${maxDurationLabel} est écoulé.');
            return false;
        }
        return true;
    }

    // ========================================
    // Local meeting end check
    // ========================================
    function checkLocalMeetingEnd() {
        if (!CONFIG.meetingEnd) return true;

        const meetingEndTime = new Date(CONFIG.meetingEnd).getTime();
        const expiryTime = meetingEndTime + CONFIG.meetingGracePeriod;

        if (Date.now() >= expiryTime) {
            showExpiredOverlay('La séance est terminée depuis plus de 15 minutes.');
            return false;
        }
        return true;
    }

    // ========================================
    // Server access check
    // ========================================
    async function checkServerAccess() {
        if (!CONFIG.serverCheckEnabled) return true;
        try {
            const url = new URL(CONFIG.apiUrl + '/api/supports/' + CONFIG.supportId + '/check-access');
            url.searchParams.append('opened_at', CONFIG.openedAt.toString());
            if (CONFIG.meetingId) {
                url.searchParams.append('meeting_id', CONFIG.meetingId.toString());
            }

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                showExpiredOverlay(data.message || 'Votre session a expiré.');
                return false;
            }

            return true;
        } catch (error) {
            // Network error - block access (app requires connectivity)
            console.error('[OnCogite] Access check error:', error);
            showExpiredOverlay('Connexion perdue. Veuillez vérifier votre connexion internet.');
            return false;
        }
    }

    // ========================================
    // Complete check
    // ========================================
    async function performCheck() {
        if (isExpired) return;

        // Local checks first (fast)
        if (!checkLocalTimer()) return;
        if (!checkLocalMeetingEnd()) return;

        // Server check
        await checkServerAccess();
    }

    // ========================================
    // Initialization
    // ========================================
    function init() {
        // Create overlay (hidden)
        createOverlay();

        // First check immediately
        performCheck();

        // Periodic checks
        checkIntervalId = setInterval(performCheck, CONFIG.checkInterval);

        // Log startup info
        const expiresAt = new Date(CONFIG.openedAt + CONFIG.maxDuration);
        console.log('[OnCogite] Access control initialized', {
            supportId: CONFIG.supportId,
            type: CONFIG.type,
            meetingId: CONFIG.meetingId,
            openedAt: new Date(CONFIG.openedAt).toISOString(),
            timerExpiresAt: expiresAt.toISOString(),
            meetingEnd: CONFIG.meetingEnd
        });
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
`;
}
